import React, { FC, Suspense, lazy } from 'react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useController, useFormContext } from 'react-hook-form';
import classNames from 'classnames';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import LoadingSpinner from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';

import { useCreateThesisImageMutation } from '@interfaces/graphql-typings';

import fieldStyles from '@buyside/components/presentation/Fields/fieldStyles.scss';
import styles from './sharedFieldsStyles.scss';

const TinyMCEEditor = lazy(() =>
  import('@buyside/components/presentation/TinyMCEEditor/tinyMceEditor')
);

const ThesisField: FC<{
  imageableId: string;
  imageableType: 'Idea' | 'WhitePaper';
  onAttachThesisInstead: () => void;
}> = ({ imageableId, imageableType, onAttachThesisInstead }) => {
  const { setValue } = useFormContext();

  const {
    field: { name, onBlur, ref, value },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ name: 'thesis' });

  const [handleCreateThesisImage] = useCreateThesisImageMutation();

  return (
    <div className={classNames(fieldStyles.field, fieldStyles.medium)}>
      <label data-title>Thesis</label>
      <div
        className={classNames(styles.editorWrapper, { [styles.error]: Boolean(error) })}
        ref={ref}
        tabIndex={-1}
      >
        <Suspense fallback={<LoadingSpinner />}>
          <TinyMCEEditor
            id={name}
            disabled={isSubmitting}
            imagesUploadConfig={{
              imageableId,
              createThesisImage: ({ imageableId, image }) =>
                handleCreateThesisImage({
                  variables: {
                    image,
                    imageableId,
                    type: imageableType
                  }
                })
            }}
            onBlur={onBlur}
            onEditorChange={value => {
              setValue('thesis', value, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
              });
            }}
            value={value}
            withImages
          />
        </Suspense>
      </div>
      {error ? (
        <label data-error>
          <FontAwesomeIcon icon={faExclamationCircle} />
          {error.message}
        </label>
      ) : null}
      <Button
        className={styles.uploadThesisButton}
        onClick={() => {
          setValue('thesis', '', {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
          });
          onAttachThesisInstead();
        }}
        size={BUTTON_SIZES.SMALL}
        text="Attach Your Thesis Instead"
        type={BUTTON_TYPES.LINK}
      />
      <small>
        Plagiarism is taken seriously and may result in account deletion or other corrective
        actions. Please cite your sources.
      </small>
      <small>
        Images will be automatically resized to 800 pixels for the longer side.{' '}
        {imageableType === 'Idea' ? (
          <>
            Please upload a formatted thesis below or your materials as a supporting document if you
            need larger images.
          </>
        ) : (
          <>
            Please upload your materials as a supporting document below if you need larger images.
          </>
        )}
      </small>
    </div>
  );
};

export default ThesisField;
